import {
  Asset,
  AssetLanguage,
  AssetMedia,
  AssetSearchResult,
  AssetStats,
  SearchAssetsQuery,
} from "@pax/api-contract/assets/v1alpha1";
import { z } from "zod";

export const AssetSearchQuery = SearchAssetsQuery.omit({
  includeSensitive: true,
  limit: true,
  cursor: true,
})
  .extend({
    includeSensitive: z.enum(["true", "false"]).default("false").meta({
      description: "`true` to include sensitive assets. Defaults to `false`.",
    }),
    limit: z.coerce.number().int().min(1).max(20).default(8).meta({
      description: "Maximum assets to return, 1–20, default 8, best match first.",
    }),
  })
  .meta({
    id: "AssetSearchQuery",
    description:
      "What an agent looks for. Collections a skill mentions go in `inAnyCollection`; the game can search public collections, private ones its owner owns or collaborates on, and private official ones if its owner curates them.",
    example: {
      q: "flag of prussia",
      kind: "image",
      inAnyCollection: "0199f0c2-1a3b-7c4d-8e5f-6a7b8c9d0e1f",
    },
  });
export type AssetSearchQuery = z.infer<typeof AssetSearchQuery>;

export const AssetMatch = z
  .strictObject({
    id: Asset.shape.id,
    versionId: Asset.shape.versionId.meta({
      description:
        "The asset's live version. A world names the asset by it, so the world keeps showing this version after the asset's next.",
    }),
    slug: Asset.shape.slug.unwrap(),
    kind: Asset.shape.kind,
    title: Asset.shape.title.unwrap(),
    description: Asset.shape.description,
    tags: Asset.shape.tags,
    language: AssetLanguage,
    isSensitive: z.boolean(),
    isAiGenerated: Asset.shape.isAiGenerated,
    isHumanVerified: Asset.shape.isHumanVerified,
    media: AssetMedia,
    stats: AssetStats,
    similarity: AssetSearchResult.shape.similarity,
  })
  .meta({
    id: "AssetMatch",
    description:
      "A published asset that matches an agent's search, at its live version. Name it in the world by `versionId`, such as an image as a polity's emblem.",
  });
export type AssetMatch = z.infer<typeof AssetMatch>;

export const AssetSearchResponse = z
  .strictObject({
    assets: z.array(AssetMatch).meta({ description: "The best matches first." }),
  })
  .meta({ id: "AssetSearchResponse", description: "The assets that best match the search." });
export type AssetSearchResponse = z.infer<typeof AssetSearchResponse>;
